// Phase 9.7 — File-driven ordering rules master data page.
// Route: /tools/fnb-po/ordering-rules

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, detailFromError } from "@/lib/api";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AlertTriangle, CheckCircle2, Download, FileText, Info, Lock, Upload } from "lucide-react";
import VendorLinksSection from "./VendorLinksSection";

// Two-step upload: preview → commit. Preview never writes.
function UploadDialog({ open, onOpenChange, onCommitted }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);

  const reset = () => {
    setFile(null); setPreview(null); setBusy(false);
    setProgress(0); setError(null);
  };

  const doPreview = useCallback(async (f) => {
    if (!f) return;
    setBusy(true); setError(null); setPreview(null); setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await api.post(
        "/fnb/admin/ordering-rules/preview", fd,
        {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (evt) => {
            if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
          },
        },
      );
      setPreview(res.data);
    } catch (e) {
      const msg = detailFromError(e);
      setError(msg);
      toast.error(`Preview failed: ${msg}`);
    } finally {
      setBusy(false);
    }
  }, []);

  const doCommit = async () => {
    if (!file) return;
    setBusy(true); setError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/fnb/admin/upload-ordering-rules", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(
        `Ingested ${res.data.config_rows_upserted} rows` +
        (res.data.manual_locked_preserved > 0
          ? ` (preserved ${res.data.manual_locked_preserved} manual)`
          : ""),
      );
      onCommitted?.(res.data);
      reset();
      onOpenChange(false);
    } catch (e) {
      const msg = detailFromError(e);
      setError(msg);
      toast.error(`Commit failed: ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  const onChoose = (e) => {
    const f = e.target.files?.[0];
    if (f) { setFile(f); doPreview(f); }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}
    >
      <DialogContent className="max-w-3xl" data-testid="ordering-rules-upload-dialog">
        <DialogHeader>
          <DialogTitle>Upload Ordering Rules</DialogTitle>
          <DialogDescription>
            One row per item: reorder level, standard order qty, purchase unit.
            Preview first, then commit.
          </DialogDescription>
        </DialogHeader>

        {!preview && (
          <label
            className={
              "block cursor-pointer rounded-md border border-dashed border-border/80 " +
              "px-6 py-10 text-center transition-colors " +
              (busy ? "bg-muted/40 cursor-wait" : "hover:bg-muted/30")
            }
            data-testid="ordering-rules-file-drop"
          >
            <input
              type="file" accept=".xlsx" className="hidden"
              onChange={onChoose} disabled={busy}
              data-testid="ordering-rules-file-input"
            />
            <div className="text-sm">Click to choose an .xlsx file</div>
            <div className="text-xs text-muted-foreground mt-1">
              We&apos;ll parse it and show you what will happen before writing.
            </div>
          </label>
        )}

        {busy && !preview && (
          <div className="space-y-1">
            <Progress value={progress} />
            <div className="text-xs text-muted-foreground text-right">
              {progress}% uploaded
            </div>
          </div>
        )}

        {error && (
          <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>
        )}

        {preview && (
          <div className="space-y-3" data-testid="ordering-rules-preview-block">
            <div className="rounded-md border border-border/60 bg-muted/30 p-3 text-sm space-y-1">
              <div className="font-medium">Preview — nothing has been written yet</div>
              <div className="text-xs text-muted-foreground">
                Parsed rows: <b data-testid="preview-parsed-rows">{preview.parsed_rows}</b>
                {" · "}Matched SKUs: <b data-testid="preview-matched-count">{preview.matched_sku_count}</b>
                {" · "}Needs review: <b data-testid="preview-needs-review-count">{preview.needs_review_count}</b>
              </div>
            </div>

            {preview.unresolved_vendors?.length > 0 && (
              <Alert data-testid="preview-unresolved-vendors">
                <AlertTitle className="flex items-center gap-1.5 text-sm">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Unresolved vendors
                </AlertTitle>
                <AlertDescription className="text-xs">
                  The file references {preview.unresolved_vendors.length} vendor
                  name(s) that don&apos;t match your <code>fb_vendors</code>{" "}
                  master. They&apos;ll be stored verbatim on each config row —
                  you can clean up later.
                  <div className="mt-1 flex flex-wrap gap-1">
                    {preview.unresolved_vendors.map((v) => (
                      <Badge key={v} variant="outline" className="text-[10px]">{v}</Badge>
                    ))}
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {preview.needs_review_count > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50/70 p-3 text-xs space-y-1">
                <div className="font-medium">Rows that need review</div>
                <div className="text-muted-foreground">
                  Low-confidence fuzzy matches — commit will skip these; add
                  them manually from the master data page.
                </div>
                <div className="max-h-32 overflow-auto mt-1 space-y-1">
                  {preview.per_row.filter((r) => !r.auto_matched).map((r) => (
                    <div key={r.row_idx} className="font-mono text-[10px]">
                      row {r.row_idx}: {r.file_name.slice(0, 50)} → {r.best_sku} ({r.best_score})
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="ghost" onClick={() => onOpenChange(false)}
            data-testid="ordering-rules-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={doCommit}
            disabled={!preview || busy}
            data-testid="ordering-rules-commit-btn"
          >
            {busy ? "Committing…" : `Commit ${preview?.matched_sku_count ?? 0} rows`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// Phase 9.7.1b — Managed banner + Download/Upload workflow for
// daily-scope items missing a `fb_item_ordering_config` row.
function UnconfiguredReviewSection() {
  const [count, setCount] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [resultOpen, setResultOpen] = useState(false);
  const [result, setResult] = useState(null);

  const loadCount = useCallback(async () => {
    try {
      const res = await api.get("/fnb/admin/unconfigured-items/count");
      setCount(res.data.count || 0);
    } catch {
      // Silent — the banner just won't render.
    }
  }, []);

  useEffect(() => { loadCount(); }, [loadCount]);

  const doDownload = async () => {
    setDownloading(true);
    try {
      const res = await api.get(
        "/fnb/admin/unconfigured-items/export.csv",
        { responseType: "blob" },
      );
      // Filename extraction from Content-Disposition — fall back to stamped default.
      const cd = res.headers["content-disposition"] || "";
      const m = /filename="([^"]+)"/.exec(cd);
      const name = m ? m[1] : "unconfigured_items_review.csv";
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = name;
      document.body.appendChild(a); a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("CSV downloaded — fill it and re-upload");
    } catch (e) {
      toast.error(detailFromError(e));
    } finally {
      setDownloading(false);
    }
  };

  const doUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(
        "/fnb/admin/unconfigured-items/import",
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setResult(res.data);
      setResultOpen(true);
      await loadCount();
    } catch (e) {
      toast.error(detailFromError(e));
    } finally {
      setUploading(false);
    }
  };

  if (count === 0) return null;

  return (
    <>
      <Card
        className="border-amber-200 bg-amber-50/50"
        data-testid="unconfigured-review-card"
      >
        <CardHeader className="pb-2 flex flex-row items-start justify-between gap-3">
          <div>
            <CardTitle className="text-sm flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Manage unconfigured items
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1 max-w-xl">
              <b data-testid="unconfigured-count">{count}</b> daily-scope
              item{count === 1 ? "" : "s"} in your catalog{" "}
              {count === 1 ? "has" : "have"} no ordering rule. Download
              the review CSV, fill in per-item actions, and re-upload.
            </p>
          </div>
        </CardHeader>
        <CardContent className="pt-1">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline" size="sm"
              onClick={doDownload}
              disabled={downloading}
              data-testid="unconfigured-download-btn"
            >
              <Download className="w-3.5 h-3.5 mr-1.5" />
              {downloading ? "Preparing…" : "Download review CSV"}
            </Button>
            <label
              className={
                "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-sm cursor-pointer " +
                (uploading
                  ? "opacity-60 cursor-not-allowed border-border/60"
                  : "border-border/70 hover:bg-background")
              }
              data-testid="unconfigured-upload-label"
            >
              <Upload className="w-3.5 h-3.5" />
              {uploading ? "Uploading…" : "Upload filled CSV"}
              <input
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                disabled={uploading}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (f) doUpload(f);
                }}
                data-testid="unconfigured-upload-input"
              />
            </label>
          </div>
        </CardContent>
      </Card>

      <Dialog open={resultOpen} onOpenChange={setResultOpen}>
        <DialogContent
          className="max-w-md"
          data-testid="unconfigured-import-result"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="w-4 h-4" />
              Import processed
            </DialogTitle>
            <DialogDescription>
              {result?.processed || 0} row{(result?.processed || 0) === 1 ? "" : "s"} scanned.
            </DialogDescription>
          </DialogHeader>
          {result && (
            <div className="text-sm space-y-1">
              <div className="flex justify-between">
                <span>Rules added</span>
                <b data-testid="unconfigured-result-added">{result.added_rules}</b>
              </div>
              <div className="flex justify-between">
                <span>Marked excluded</span>
                <b>{result.marked_excluded}</b>
              </div>
              <div className="flex justify-between">
                <span>Marked inactive</span>
                <b>{result.marked_inactive}</b>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Kept unconfigured (no-op)</span>
                <b>{result.kept}</b>
              </div>
              {result.errors && result.errors.length > 0 && (
                <div className="pt-2 mt-2 border-t border-border/60">
                  <div className="text-xs font-medium text-rose-800 mb-1">
                    {result.errors.length} error
                    {result.errors.length === 1 ? "" : "s"}
                  </div>
                  <ul className="text-[11px] text-rose-800 space-y-0.5 max-h-40 overflow-auto">
                    {result.errors.map((err, i) => (
                      <li key={i} data-testid={`unconfigured-error-${i}`}>
                        row {err.row}: {err.reason}
                        {err.sku ? ` (${err.sku})` : ""}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              size="sm"
              onClick={() => setResultOpen(false)}
              data-testid="unconfigured-result-close"
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


function EditRow({ row, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [reorder, setReorder] = useState(row.reorder_level_qty);
  const [stdQty, setStdQty] = useState(row.standard_order_qty);
  const [saving, setSaving] = useState(false);
  const [confirmAnomaly, setConfirmAnomaly] = useState(false);

  // Phase 9.7.1b sanity guard — the standard order qty is displayed
  // in the item's `standard_order_unit`, but the anomaly threshold is
  // computed against `standard_order_qty_in_measuring_unit` because
  // that's what the reorder threshold is denominated in.
  const stdMeasuring = Number(row.standard_order_qty_in_measuring_unit) || 0;
  const ratio = stdMeasuring > 0 ? Number(reorder) / stdMeasuring : 0;
  const anomalous = stdMeasuring > 0 && Number(reorder) > 5 * stdMeasuring;

  const doSave = async () => {
    setSaving(true);
    try {
      const res = await api.patch(
        `/fnb/item-ordering-config/${encodeURIComponent(row.item_sku)}`,
        {
          reorder_level_qty: Number(reorder),
          standard_order_qty: Number(stdQty),
        },
      );
      toast.success(`${row.item_sku} updated (manual-locked)`);
      onSaved?.(res.data);
      setEditing(false);
      setConfirmAnomaly(false);
    } catch (e) {
      toast.error(detailFromError(e));
    } finally {
      setSaving(false);
    }
  };

  const save = () => {
    if (anomalous) {
      setConfirmAnomaly(true);
      return;
    }
    doSave();
  };

  if (!editing) {
    return (
      <TableRow data-testid={`ordering-row-${row.item_sku}`}>
        <TableCell className="font-mono text-xs">{row.item_sku}</TableCell>
        <TableCell className="text-xs">{row.item_name || <span className="text-muted-foreground italic">—</span>}</TableCell>
        <TableCell className="text-xs">{row.vendor_name}</TableCell>
        <TableCell className="text-right tabular-nums text-xs">
          {row.reorder_level_qty} <span className="text-muted-foreground">{row.measuring_unit}</span>
        </TableCell>
        <TableCell className="text-right tabular-nums text-xs">
          {row.standard_order_qty} {row.standard_order_unit}
        </TableCell>
        <TableCell className="text-xs">
          {row.mapping_source === "manual" ? (
            <Badge variant="outline" className="text-[10px] gap-0.5"
                   data-testid={`ordering-lock-${row.item_sku}`}>
              <Lock className="w-2.5 h-2.5" /> Manual
            </Badge>
          ) : (
            <Badge variant="secondary" className="text-[10px]">Auto</Badge>
          )}
          {row.unit_mismatch_flag && (
            <Badge variant="outline" className="text-[10px] ml-1 border-amber-400 text-amber-800">
              <Info className="w-2.5 h-2.5 mr-0.5" /> unit mismatch
            </Badge>
          )}
        </TableCell>
        <TableCell>
          <Button
            size="sm" variant="ghost"
            onClick={() => setEditing(true)}
            data-testid={`ordering-edit-${row.item_sku}`}
          >
            Edit
          </Button>
        </TableCell>
      </TableRow>
    );
  }
  return (
    <>
    <TableRow data-testid={`ordering-row-${row.item_sku}-editing`}>
      <TableCell className="font-mono text-xs">{row.item_sku}</TableCell>
      <TableCell className="text-xs">{row.item_name}</TableCell>
      <TableCell className="text-xs">{row.vendor_name}</TableCell>
      <TableCell className="text-right">
        <Input
          type="number" min={0} step="0.1"
          value={reorder}
          onChange={(e) => setReorder(e.target.value)}
          className={
            "h-7 text-xs w-24 ml-auto " +
            (anomalous ? "border-amber-500 focus-visible:ring-amber-500" : "")
          }
          data-testid={`ordering-input-reorder-${row.item_sku}`}
        />
        {anomalous && (
          <div
            className="mt-1 text-[10px] text-amber-700 whitespace-nowrap"
            data-testid={`ordering-anomaly-hint-${row.item_sku}`}
          >
            ⚠ {ratio.toFixed(0)}× your std order qty
          </div>
        )}
      </TableCell>
      <TableCell className="text-right">
        <Input
          type="number" min={0.001} step="0.1"
          value={stdQty}
          onChange={(e) => setStdQty(e.target.value)}
          className="h-7 text-xs w-24 ml-auto"
          data-testid={`ordering-input-stdqty-${row.item_sku}`}
        />
      </TableCell>
      <TableCell className="text-xs">
        <Badge variant="outline" className="text-[10px]">→ Manual on save</Badge>
      </TableCell>
      <TableCell className="flex gap-1">
        <Button
          size="sm" onClick={save} disabled={saving}
          data-testid={`ordering-save-${row.item_sku}`}
        >
          {saving ? "…" : "Save"}
        </Button>
        <Button
          size="sm" variant="ghost" onClick={() => setEditing(false)}
        >
          Cancel
        </Button>
      </TableCell>
    </TableRow>
    <Dialog open={confirmAnomaly} onOpenChange={setConfirmAnomaly}>
      <DialogContent
        className="max-w-md"
        data-testid={`ordering-anomaly-dialog-${row.item_sku}`}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-800">
            <AlertTriangle className="w-4 h-4" />
            Reorder level looks unusually high
          </DialogTitle>
          <DialogDescription className="pt-2 space-y-2 text-sm">
            <div>
              You&apos;re setting <b>{row.item_sku}</b> reorder level to{" "}
              <b>{Number(reorder).toLocaleString()}</b> {row.measuring_unit}, which
              is <b>{ratio.toFixed(0)}×</b> higher than your standard order
              quantity ({stdMeasuring.toLocaleString()} {row.measuring_unit}).
            </div>
            <div className="text-xs text-muted-foreground">
              Typical reorder levels are lower than your order size so a
              new order arrives <em>before</em> stock runs out. A ratio of
              5× or more is usually a data-entry typo. Continue anyway?
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => setConfirmAnomaly(false)}
            data-testid={`ordering-anomaly-cancel-${row.item_sku}`}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={doSave}
            disabled={saving}
            data-testid={`ordering-anomaly-confirm-${row.item_sku}`}
          >
            {saving ? "Saving…" : "Yes, save it"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}


export default function FnbPoOrderingRules() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [vendor, setVendor] = useState("");
  const [search, setSearch] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "500" });
      if (vendor) params.set("vendor_name", vendor);
      if (search) params.set("search", search);
      const res = await api.get(`/fnb/item-ordering-config?${params}`);
      setRows(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      toast.error(detailFromError(e));
    } finally {
      setLoading(false);
    }
  }, [vendor, search]);

  useEffect(() => { load(); }, [load]);

  const vendors = Array.from(new Set(rows.map((r) => r.vendor_name))).sort();
  const manualCount = rows.filter((r) => r.mapping_source === "manual").length;

  return (
    <div className="space-y-6" data-testid="fnb-po-ordering-rules">
      <header className="flex items-center justify-between">
        <div className="space-y-1">
          <h1
            className="text-2xl font-semibold tracking-tight"
            data-testid="ordering-rules-heading"
          >
            Item Ordering Rules
          </h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            File-driven per-item reorder levels + fixed purchase quantities.
            Used by the &ldquo;File rules&rdquo; PO strategy in the auto-suggest wizard.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="ghost" size="sm" data-testid="ordering-rules-back">
            <Link to="/tools/fnb-po">← Dashboard</Link>
          </Button>
          <Button
            onClick={() => setUploadOpen(true)}
            data-testid="ordering-rules-upload-btn"
          >
            <Upload className="w-4 h-4 mr-1" /> Upload rules
          </Button>
        </div>
      </header>

      <UnconfiguredReviewSection />

      <VendorLinksSection />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">
              Total configs
            </CardTitle>
          </CardHeader>
          <CardContent className="text-2xl font-semibold" data-testid="ordering-total">
            {total}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">
              Manual-locked
            </CardTitle>
          </CardHeader>
          <CardContent
            className="text-2xl font-semibold flex items-center gap-2"
            data-testid="ordering-manual-count"
          >
            <Lock className="w-4 h-4 text-muted-foreground" />
            {manualCount}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">
              Vendors
            </CardTitle>
          </CardHeader>
          <CardContent className="text-2xl font-semibold" data-testid="ordering-vendors">
            {vendors.length}
          </CardContent>
        </Card>
      </div>

      <div className="flex gap-3 items-center">
        <Input
          placeholder="Search SKU…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
          data-testid="ordering-search-input"
        />
        <select
          value={vendor}
          onChange={(e) => setVendor(e.target.value)}
          className="h-10 px-3 rounded-md border border-input bg-background text-sm"
          data-testid="ordering-vendor-filter"
        >
          <option value="">All vendors</option>
          {vendors.map((v) => (
            <option key={v} value={v}>{v}</option>
          ))}
        </select>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table data-testid="ordering-rules-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-24">SKU</TableHead>
                  <TableHead>Item</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead className="text-right w-36">Reorder Level</TableHead>
                  <TableHead className="text-right w-32">Std Order Qty</TableHead>
                  <TableHead className="w-40">Source</TableHead>
                  <TableHead className="w-24" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-10">
                      Loading…
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-10 space-y-2">
                      <div className="text-sm text-muted-foreground">
                        No ordering rules yet.
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setUploadOpen(true)}
                        data-testid="ordering-rules-empty-upload"
                      >
                        <Upload className="w-4 h-4 mr-1" /> Upload the file
                      </Button>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((r) => (
                    <EditRow
                      key={r.item_sku}
                      row={r}
                      onSaved={(fresh) => {
                        setRows((prev) => prev.map(
                          (x) => x.item_sku === fresh.item_sku
                            ? { ...x, ...fresh } : x,
                        ));
                      }}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <UploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        onCommitted={() => load()}
      />

      {!loading && rows.length > 0 && (
        <div className="text-xs text-muted-foreground flex items-center gap-1.5">
          <CheckCircle2 className="w-3 h-3 text-emerald-600" />
          Showing {rows.length} of {total}.{" "}
          Manual-locked rows survive re-uploads unchanged.
        </div>
      )}
    </div>
  );
}
